import { useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { accountantReportDeliveryPatchSchema, type AccountantReportDeliverySettings, type AccountantReportDeliveryPatch } from 'shared-models/browser';
import { accountantReportService } from '../../../../services/accountant-report/accountant-report.service';
import { toHebrewApiErrorMessage } from '../../../../services/index/index.service';

export function useAccountantReportDelivery({ opened, tenantId, employeeSession }: {
  opened: boolean; tenantId: string; employeeSession: string;
}) {
  const [form, setForm] = useState({ recipientsText: '', dayOfMonth: 1 });
  const [baseline, setBaseline] = useState<AccountantReportDeliverySettings | null>(null);
  const baselineRef = useRef<AccountantReportDeliverySettings | null>(null);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const savingRef = useRef(false);
  const parsed = accountantReportDeliveryPatchSchema.safeParse({
    recipients: form.recipientsText.split(/[\s,;]+/).filter(Boolean), dayOfMonth: form.dayOfMonth,
  });
  const patch: AccountantReportDeliveryPatch = {};
  if (baseline && parsed.success) {
    if (JSON.stringify(parsed.data.recipients) !== JSON.stringify(baseline.recipients)) patch.recipients = parsed.data.recipients;
    if (form.dayOfMonth !== baseline.dayOfMonth) patch.dayOfMonth = form.dayOfMonth;
  }

  useEffect(() => {
    if (!opened || !employeeSession) return;
    let cancelled = false;
    baselineRef.current = null;
    setBaseline(null);
    setLoadingDelivery(true);
    const applySettings = (settings: AccountantReportDeliverySettings) => {
      if (cancelled || !settings) return;
      const previous = baselineRef.current;
      setForm((current) => {
        const currentEmails = accountantReportDeliveryPatchSchema.safeParse({ recipients: current.recipientsText.split(/[\s,;]+/).filter(Boolean) });
        const unchangedEmails = !previous || (currentEmails.success && JSON.stringify(currentEmails.data.recipients) === JSON.stringify(previous.recipients));
        return {
          recipientsText: unchangedEmails ? settings.recipients.join('\n') : current.recipientsText,
          dayOfMonth: !previous || current.dayOfMonth === previous.dayOfMonth ? settings.dayOfMonth : current.dayOfMonth,
        };
      });
      baselineRef.current = settings;
      setBaseline(settings);
    };
    const onUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId: string; settings: AccountantReportDeliverySettings }>).detail;
      if (detail.tenantId === tenantId) applySettings(detail.settings);
    };
    window.addEventListener('accountant-report-delivery-updated', onUpdated);
    const initialBaseline = baselineRef.current;
    void accountantReportService.getDeliverySettings(tenantId, employeeSession).then((settings) => {
      if (baselineRef.current === initialBaseline) applySettings(settings);
    }).catch((error: unknown) => {
      if (!cancelled) notifications.show({ color: 'red', message: toHebrewApiErrorMessage((error as Error).message) });
    }).finally(() => { if (!cancelled) setLoadingDelivery(false); });
    return () => {
      cancelled = true;
      window.removeEventListener('accountant-report-delivery-updated', onUpdated);
    };
  }, [opened, tenantId, employeeSession]);

  async function saveDelivery(): Promise<void> {
    if (!baseline || !parsed.success || Object.keys(patch).length === 0 || savingRef.current) return;
    savingRef.current = true;
    setSavingDelivery(true);
    try {
      await accountantReportService.updateDeliverySettings(tenantId, patch, employeeSession);
      notifications.show({ color: 'green', message: 'הגדרות השליחה נשמרו' });
    } catch (error) {
      notifications.show({ color: 'red', message: toHebrewApiErrorMessage((error as Error).message) });
    } finally {
      savingRef.current = false;
      setSavingDelivery(false);
    }
  }

  return {
    recipientsText: form.recipientsText,
    setRecipientsText: (recipientsText: string) => setForm((current) => ({ ...current, recipientsText })),
    deliveryDay: form.dayOfMonth,
    setDeliveryDay: (dayOfMonth: number) => setForm((current) => ({ ...current, dayOfMonth })),
    canSaveDelivery: parsed.success && !!baseline && Object.keys(patch).length > 0,
    deliveryInvalid: !parsed.success,
    loadingDelivery, savingDelivery, saveDelivery,
    lastDelivery: baseline?.lastDelivery ?? null,
  };
}